import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Truck, Leaf, Award } from 'lucide-react';

const Locations: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const regions = [
    // São Paulo Capital e região
    "São Paulo (capital) - Penha",
    "Santo André",
    "São Mateus",
    "Diadema",
    "Itaquera",
    "Parelheiros",
    "Guarulhos",
    "Jabaquara",
    "Mauá",
    "Aricanduva",
    "Lapa",
    "São Miguel",
    "Jagana",
    "Interlagos",
    "Pirituba",
    "Pinheiros",
    "Campos Elísios",
    "Higienópolis",
    // Litoral
    "Praia Grande",
    "Santos"
  ];

  return (
    <section ref={sectionRef} id="locations" className="py-20 bg-gray-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
            Regiões Atendidas
          </h2>
          <p className="text-xl text-primary-700 max-w-3xl mx-auto">
            <span className="font-semibold text-primary-800">CONHEÇA SOL NASCENTE HORTALIÇAS</span><br />
            <span className="text-lg">PRODUTOR E DISTRIBUIDOR DE VERDURAS E LEGUMES</span>
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Leaf className="text-primary-600" size={24} />
            <span className="text-primary-700 font-semibold text-lg">PRODUZINDO QUALIDADE DE VIDA!</span>
            <Leaf className="text-primary-600" size={24} />
          </div>
        </div>

        {/* Company Location */}
        <div className={`bg-white rounded-2xl shadow-xl p-8 mb-12 transition-all duration-1000 delay-200 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <MapPin className="text-primary-600" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-primary-800 mb-1">Nossa Sede</h3>
              <p className="text-primary-600">Localizada em Ibiúna - SP</p>
            </div>
          </div>
          {/* Sede Image e texto lado a lado - espaçamentos ajustados */}
          <div className="flex flex-col md:flex-row items-center md:items-center gap-1">
            <div className="flex justify-center md:justify-start md:w-1/2">
              <img
                src="/img/sede.jpg"
                alt="Foto da sede da Sol Nascente"
                className="rounded-2xl shadow-md border border-primary-100 object-cover"
                style={{ maxWidth: 320, width: '100%', height: 'auto', maxHeight: 180, marginBottom: 0 }}
              />
            </div>
            <div >
              <div className="flex items-center  mb-1">
                <Award className="text-primary-600" size={50} />
                <span className="text-primary-700 font-semibold">
                  Distribuição direta do produtor para as principais regiões de São Paulo
                </span>
              </div>
              <p className="text-primary-600 leading-relaxed m-0">
                Não possuímos filiais. Trabalhamos com distribuição direta de nossa sede em Ibiúna-SP,<br/>
                garantindo produtos frescos e de qualidade para todas as regiões atendidas.
              </p>
            </div>
          </div>
        </div>

        {/* Regions Grid */}
        <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-primary-800 mb-2">Regiões Atendidas</h3>
            <p className="text-primary-600">Levamos frescor e qualidade para toda a Grande São Paulo</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {regions.map((region, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 border-l-4 border-primary-500 ${
                  index % 2 === 0 ? 'animate-fade-in-left' : 'animate-fade-in-right'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <Truck className="text-primary-600" size={16} />
                  </div>
                  <span className="text-primary-800 font-medium">{region}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-600 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-primary-600 text-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              Sua região não está na lista?
            </h3>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              Entre em contato conosco! Estamos sempre expandindo nossa área de atendimento 
              para levar produtos frescos e de qualidade para mais regiões.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/551532415413?text=Olá! Gostaria de saber se vocês atendem minha região. Falar com setor comercial (Gabriel)." 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-200 flex items-center justify-center gap-3"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.109"/>
                </svg>
                <span>WhatsApp</span>
              </a>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Falar Conosco
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Locations;
